import React, { useEffect, useState } from 'react';
import P, { InferProps } from 'prop-types';
import { SxStyle } from '../../../types';
import SelectField from './select-field';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Typography from '@mui/material/Typography';
import { PROMO_VARIANT } from '../../../types/team';
import PromoFieldFlarie from './promo-field-flarie';
import PromoFieldImage from './promo-field-image';
import PromoFieldVideo from './promo-field-video';
import PromoFieldPoll from './promo-field-poll';
import { Field } from '../../../types';
import * as yup from 'yup';
import PromoFieldSubscription from './promo-field-subscription';
import { v4 as uuidv4 } from 'uuid';

const styles: SxStyle = {
  tableRow: {
    marginTop: '6px',
    '&:last-child td, &:last-child th': {
      border: 0,
    },
  },
  tableCellInfo: {
    minWidth: 350,
  },
};

function PromoField({
  setChange,
  setValidation,
  title,
  fields,
  isReadOnly,
  path,
  setPromosInUse,
}: InferProps<typeof PromoField.propTypes>) {
  const [selectedVariant, setSelectedVariant] = useState<string | null>(null);

  /* Disabled for now, until we make sceneconfig-preview is working again
  // TODO fix
  const [mobileSceneConfig, loadingMobile] = useCollection<Record<'scene', SceneConfig>[]>(
    collection(firebaseApp.db, `app-configs/${path}-${DEVICES.MOBILE}/details`)
  );
  // TODO fix
  const [tabletSceneConfig, loadingTablet] = useCollection<Record<'scene', SceneConfig>[]>(
    collection(firebaseApp.db, `app-configs/${path}-${DEVICES.TABLET}/details`)
  );

  useEffect(() => {
    if (setPromosInUse && mobileSceneConfig && tabletSceneConfig) {
      const mobilePromos = convertToPromoField(mobileSceneConfig);
      const tabletPromos = convertToPromoField(tabletSceneConfig);

      const moduleIndex: string = fields[0].id.slice(-2).replace('-', '');
      const scenesOnMobile = getSceneNames(mobilePromos, moduleIndex);
      const scenesOnTablet = getSceneNames(tabletPromos, moduleIndex);

      const keyOfCurrentPromoInUse: keyof PromosInUse = fields[0].title
        .split(' ')
        .slice(0, 2)
        .join('-')
        .toLowerCase();
      const mobileScenesInUse = `${scenesOnMobile.join(',')}`;
      const tabletScenesInUse = `${scenesOnTablet.join(',')}`;
      const updatedTeamPromoScenes: Partial<PromosInUse> = {
        [keyOfCurrentPromoInUse]: { mobile: mobileScenesInUse, tablet: tabletScenesInUse },
      };

      setPromosInUse(updatedTeamPromoScenes);
    }
  }, [fields, mobileSceneConfig, setPromosInUse, tabletSceneConfig]);

  if (loadingMobile && loadingTablet) {
    return <p>{strings.messages.loading}</p>;
  }
  */

  useEffect(() => {
    setSelectedVariant(fields[1].value);
  }, [fields]);

  const onChangeType = (
    fieldKey: string,
    value: string | number | boolean,
    fieldValidation: any,
    isTeamDataValue: boolean
  ) => {
    if (fieldKey.includes('promo_type')) {
      setSelectedVariant(value as string);
    }

    // If the promo type is none, then we remove the promo by setting it to null (i.e. promo[index] = null)
    if (fieldKey.includes('promo_type') && value === PROMO_VARIANT.NONE) {
      const fieldKeyPromoOnly = fieldKey.replace('.promo_type', '');
      setChange(fieldKeyPromoOnly, null, fieldValidation, isTeamDataValue);
      // Make sure all fields are validated as true again when promo is removed
      fields.forEach((field: Field) => {
        setValidation(field.key, true, yup.boolean());
      });
    } else {
      setChange(fieldKey, value, fieldValidation, isTeamDataValue);
    }
  };

  const onChange = (
    fieldKey: string,
    value: string | number | boolean,
    fieldValidation: any,
    isTeamDataValue: boolean
  ) => {
    setChange(fieldKey, value, fieldValidation, isTeamDataValue);

    // Ugly hack to set/update promo id when values changess
    setChange(fieldKey.split('.')[0] + '.id', uuidv4(), yup.string(), false);
  };

  return (
    <TableRow
      key={fields[0].id}
      sx={styles.tableRow}
      style={{ height: selectedVariant === PROMO_VARIANT.NONE ? 50 : 180 }}
    >
      <TableCell component="th" scope="row">
        <Typography variant="body2-bold" style={{ textTransform: 'capitalize' }}>
          {title}
        </Typography>
      </TableCell>
      <TableCell>
        <SelectField
          field={fields[1]}
          setChange={onChangeType}
          key={fields[1].id}
          isReadOnly={isReadOnly}
          minWidth={150}
          fullWidth
        />
      </TableCell>
      <TableCell sx={styles.tableCellInfo}>
        {selectedVariant === PROMO_VARIANT.IMAGE && (
          <PromoFieldImage
            setChange={onChange}
            setValidation={setValidation}
            path={path}
            fields={fields}
            isReadOnly={isReadOnly}
          />
        )}
        {selectedVariant === PROMO_VARIANT.SUBSCRIPTION && (
          <PromoFieldSubscription
            setChange={setChange}
            setValidation={setValidation}
            path={path}
            fields={fields}
            isReadOnly={isReadOnly}
          />
        )}
        {selectedVariant === PROMO_VARIANT.FORZASYS_VIDEO && (
          <PromoFieldVideo
            setChange={onChange}
            setValidation={setValidation}
            path={path}
            fields={fields}
            isReadOnly={isReadOnly}
          />
        )}
        {selectedVariant === PROMO_VARIANT.POLL && (
          <PromoFieldPoll
            setChange={onChange}
            setValidation={setValidation}
            path={path}
            fields={fields}
            isReadOnly={isReadOnly}
          />
        )}
        {selectedVariant === PROMO_VARIANT.FLARIE && (
          <PromoFieldFlarie
            setChange={onChange}
            setValidation={setValidation}
            path={path}
            fields={fields}
            isReadOnly={isReadOnly}
          />
        )}
      </TableCell>
    </TableRow>
  );
}

PromoField.propTypes = {
  setChange: P.func.isRequired,
  setValidation: P.func.isRequired,
  isReadOnly: P.bool,
  fields: P.any.isRequired,
  key: P.string,
  path: P.string.isRequired,
  setPromosInUse: P.func,
  title: P.string,
};

export default PromoField;
